"use client"

import { useState, useEffect, useRef } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Activity, Globe, Smartphone, Monitor, Bot, Users, Search, RefreshCw, Eye, CheckCircle } from "lucide-react"

interface LiveMonitorContentProps {
  liveVisitors: any[]
  botActivity: any[]
  systemStats: any
}

export function LiveMonitorContent({ liveVisitors, botActivity, systemStats }: LiveMonitorContentProps) {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [refreshInterval, setRefreshInterval] = useState(3)
  const [filterCountry, setFilterCountry] = useState("all")
  const [filterStatus, setFilterStatus] = useState("all")
  const [filterDevice, setFilterDevice] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [viewMode, setViewMode] = useState<"visitors" | "bots">("visitors")
  
  // 🔹 2. Tambahkan state baru untuk debounce & pause
  const [debouncedQuery, setDebouncedQuery] = useState("")
  const [isPaused, setIsPaused] = useState(false)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  // 🔹 3. Debounce untuk pencarian (searchQuery → debouncedQuery)
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedQuery(searchQuery)
    }, 500) // debounce 500ms

    return () => clearTimeout(timeout)
  }, [searchQuery])

  // 🔹 4. Auto-refresh logic yang lebih manusiawi
  useEffect(() => {
    if (!autoRefresh || isPaused) return

    timerRef.current = setInterval(() => {
      window.location.reload() // atau trigger refetch API
    }, refreshInterval * 1000)

    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [autoRefresh, refreshInterval, isPaused])

  // Add safety checks for arrays
  const safeVisitors = Array.isArray(liveVisitors) ? liveVisitors : []
  const safeBotActivity = Array.isArray(botActivity) ? botActivity : []

  const filteredVisitors = safeVisitors.filter((visitor) => {
    const matchesCountry = filterCountry === "all" || visitor.country === filterCountry
    const matchesStatus = filterStatus === "all" || visitor.status === filterStatus
    const matchesDevice = filterDevice === "all" || visitor.device === filterDevice
    // 🔹 6. Ubah pencarian dari searchQuery ke debouncedQuery
    const matchesSearch =
      debouncedQuery === "" ||
      visitor.ip.toLowerCase().includes(debouncedQuery.toLowerCase()) ||
      visitor.userAgent.toLowerCase().includes(debouncedQuery.toLowerCase())

    return matchesCountry && matchesStatus && matchesDevice && matchesSearch
  })

  const filteredBotActivity = safeBotActivity.filter((activity) => {
    // 🔹 6. Ubah pencarian dari searchQuery ke debouncedQuery
    const matchesSearch =
      debouncedQuery === "" ||
      (activity.ip && activity.ip.toLowerCase().includes(debouncedQuery.toLowerCase())) ||
      (activity.pattern && activity.pattern.toLowerCase().includes(debouncedQuery.toLowerCase()))

    return matchesSearch
  })

  const getStatusColor = (status: string) => {
    switch (status) {
      case "legitimate":
        return "bg-green-500/20 text-green-400 border-green-500/50"
      case "bot":
        return "bg-red-500/20 text-red-400 border-red-500/50"
      case "suspicious":
        return "bg-amber-500/20 text-amber-400 border-amber-500/50"
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-400"
    if (score >= 50) return "text-amber-400"
    return "text-red-400"
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case "blocked":
        return "bg-red-500/20 text-red-400 border-red-500/50"
      case "challenged":
        return "bg-amber-500/20 text-amber-400 border-amber-500/50"
      case "monitored":
        return "bg-blue-500/20 text-blue-400 border-blue-500/50"
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-500/50"
    }
  }

  const countries = [...new Set(safeVisitors.map((v) => v.country))].sort()

  return (
    <>
      {/* Live Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {/* ... (unchanged card content) ... */}
      </div>

      {/* Controls and Filters */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-slate-100 flex items-center">
              <Eye className="mr-2 h-5 w-5 text-cyan-500" />
              Live Traffic Monitor
            </CardTitle>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Label htmlFor="auto-refresh" className="text-sm text-slate-300">
                  Auto Refresh
                </Label>
                <Switch id="auto-refresh" checked={autoRefresh} onCheckedChange={setAutoRefresh} />
              </div>
              <Select value={refreshInterval.toString()} onValueChange={(value) => setRefreshInterval(Number(value))}>
                <SelectTrigger className="w-24 bg-slate-800/50 border-slate-600">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1s</SelectItem>
                  <SelectItem value="3">3s</SelectItem>
                  <SelectItem value="5">5s</SelectItem>
                  <SelectItem value="10">10s</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {/* ... (unchanged view mode toggle) ... */}
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search IP, User Agent..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-64 bg-slate-800/50 border-slate-600"
              />
            </div>
            {/* ... (unchanged filters) ... */}
          </div>
          <div className="text-sm text-slate-400">
            Showing {viewMode === "visitors" ? filteredVisitors.length : filteredBotActivity.length} of{" "}
            {viewMode === "visitors" ? safeVisitors.length : safeBotActivity.length} entries
            {autoRefresh && !isPaused && <span className="ml-2 text-green-400">• Auto-refreshing every {refreshInterval}s</span>}
            {isPaused && <span className="ml-2 text-amber-400">• Refresh paused</span>}
          </div>
        </CardContent>
      </Card>

      {/* Live Data Display */}
      <Card className="bg-slate-900/50 border-slate-700/50 backdrop-blur-sm">
        <CardContent className="p-6">
          {viewMode === "visitors" ? (
            <div className="space-y-4">
              {filteredVisitors.length === 0 ? (
                <div className="text-center py-8 text-slate-400">No visitors match the current filters</div>
              ) : (
                filteredVisitors.map((visitor) => (
                  // 🔹 5. Hover = Pause Auto Refresh
                  <div
                    key={visitor.id}
                    className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:bg-slate-800/70 transition-colors"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                  >
                    {/* ... (unchanged visitor details) ... */}
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBotActivity.length === 0 ? (
                <div className="text-center py-8 text-slate-400">No bot activity matches the current filters</div>
              ) : (
                filteredBotActivity.map((activity) => (
                  // 🔹 5. Hover = Pause Auto Refresh
                  <div
                    key={activity.id}
                    className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50 hover:bg-slate-800/70 transition-colors"
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                  >
                    {/* ... (unchanged bot activity details) ... */}
                  </div>
                ))
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  )
}
